import * as React from 'react';
import { GUVEN_ESIGI, tcknGecerliMi, type IdentityOcrResult, type MisafirGirdi } from '@hotelcms/shared';
import { Alan, Secim } from '@/components/ui/field';
import { Rozet } from '@/components/ui/card';

interface Props {
  ocr: IdentityOcrResult | null;
  deger: MisafirGirdi;
  onChange: (yeni: MisafirGirdi) => void;
}

/** OCR ciktisini forma cevirir; okunamayan alanlar bos kalir. */
export function ocrdanMisafir(ocr: IdentityOcrResult | null): MisafirGirdi {
  const a = ocr?.alanlar;
  return {
    ad: a?.ad ?? '',
    soyad: a?.soyad ?? '',
    tckn: a?.tckn ?? null,
    pasaportNo: a?.pasaportNo ?? null,
    uyruk: a?.uyruk ?? 'TUR',
    dogumTarihi: a?.dogumTarihi ?? null,
    anneAdi: a?.anneAdi ?? null,
    babaAdi: a?.babaAdi ?? null,
    dogumYeri: a?.dogumYeri ?? null,
    cinsiyet: a?.cinsiyet ?? 'BILINMIYOR',
    belgeSeriNo: a?.seriNo ?? null,
    telefon: null,
    eposta: null,
    anaMisafir: true,
  };
}

const UYRUKLAR = [
  { deger: 'TUR', metin: 'Türkiye (TUR)' },
  { deger: 'DEU', metin: 'Almanya (DEU)' },
  { deger: 'GBR', metin: 'Birlesik Krallik (GBR)' },
  { deger: 'RUS', metin: 'Rusya (RUS)' },
  { deger: 'UKR', metin: 'Ukrayna (UKR)' },
  { deger: 'NLD', metin: 'Hollanda (NLD)' },
  { deger: 'FRA', metin: 'Fransa (FRA)' },
  { deger: 'USA', metin: 'ABD (USA)' },
];

/**
 * Adim 1'in ikinci yarisi: otomatik dolan form.
 *
 * Vurgu kurallari:
 *  - guven < 0.85 olan alan SARI
 *  - MRZ ile celisen alan SARI
 *  - KBS icin zorunlu ama bos alan KIRMIZI
 * Resepsiyonist her alani duzeltebilir; hicbiri kilitli degildir.
 */
export function MisafirFormu({ ocr, deger, onChange }: Props) {
  const dikkat = new Set(ocr?.dogrulama.dikkatGerektirenAlanlar ?? []);
  const eksik = new Set(ocr?.dogrulama.kbsEksikAlanlar ?? []);
  const guven = ocr?.alanlar.alanGuvenleri;
  const tcVatandasi = deger.uyruk === 'TUR';

  const yaz = <K extends keyof MisafirGirdi>(alan: K, v: MisafirGirdi[K]) => onChange({ ...deger, [alan]: v });
  const bosaMi = (v: unknown) => v === null || v === undefined || v === '';

  const tcknHatasi =
    deger.tckn && !tcknGecerliMi(deger.tckn)
      ? 'T.C. kimlik numarası doğrulanamadı. Rakamları kontrol edin.'
      : tcVatandasi && bosaMi(deger.tckn) && eksik.has('tckn')
        ? 'KBS bildirimi için zorunlu.'
        : null;

  return (
    <div className="flex flex-col gap-5">
      {ocr && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl bg-slate-100 px-4 py-3">
          <Rozet renk="bilgi">{ocr.alanlar.belgeTipi.replaceAll('_', ' ')}</Rozet>
          {ocr.dogrulama.tcknGecerli === true && <Rozet renk="basari">TCKN doğrulandı</Rozet>}
          {ocr.dogrulama.mrzGecerli === true && <Rozet renk="basari">MRZ doğrulandı</Rozet>}
          {ocr.dogrulama.mrzGecerli === false && <Rozet renk="uyari">MRZ kontrol hanesi tutmadı</Rozet>}
          {ocr.dogrulama.resitDegil && <Rozet renk="uyari">18 yaş altı — refakatçi gerekir</Rozet>}
          <span className="ml-auto text-sm text-slate-500">{(ocr.meta.sureMs / 1000).toFixed(1)} sn</span>
        </div>
      )}

      {(ocr?.alanlar.uyarilar.length ?? 0) > 0 && (
        <ul className="list-inside list-disc rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {ocr!.alanlar.uyarilar.map((u) => (
            <li key={u}>{u}</li>
          ))}
        </ul>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Alan
          etiket="Ad"
          value={deger.ad}
          guven={guven?.ad}
          dikkat={dikkat.has('ad') || eksik.has('ad')}
          onChange={(e) => yaz('ad', e.target.value)}
          autoFocus
        />
        <Alan
          etiket="Soyad"
          value={deger.soyad}
          guven={guven?.soyad}
          dikkat={dikkat.has('soyad') || eksik.has('soyad')}
          onChange={(e) => yaz('soyad', e.target.value)}
        />

        <Secim
          etiket="Uyruk"
          value={deger.uyruk}
          dikkat={dikkat.has('uyruk')}
          secenekler={UYRUKLAR}
          onChange={(e) => yaz('uyruk', e.target.value)}
        />
        <Secim
          etiket="Cinsiyet"
          value={deger.cinsiyet}
          dikkat={dikkat.has('cinsiyet') || eksik.has('cinsiyet')}
          secenekler={[
            { deger: 'BILINMIYOR', metin: 'Seçiniz' },
            { deger: 'K', metin: 'Kadın' },
            { deger: 'E', metin: 'Erkek' },
          ]}
          onChange={(e) => yaz('cinsiyet', e.target.value as MisafirGirdi['cinsiyet'])}
        />

        {tcVatandasi ? (
          <Alan
            etiket="T.C. Kimlik No"
            inputMode="numeric"
            maxLength={11}
            value={deger.tckn ?? ''}
            guven={guven?.tckn}
            dikkat={dikkat.has('tckn')}
            hata={tcknHatasi}
            onChange={(e) => yaz('tckn', e.target.value.replace(/\D/g, '') || null)}
          />
        ) : (
          <Alan
            etiket="Pasaport No"
            value={deger.pasaportNo ?? ''}
            guven={guven?.pasaportNo}
            dikkat={dikkat.has('pasaportNo')}
            hata={eksik.has('pasaportNo') && bosaMi(deger.pasaportNo) ? 'KBS bildirimi için zorunlu.' : null}
            onChange={(e) => yaz('pasaportNo', e.target.value.toUpperCase() || null)}
          />
        )}

        <Alan
          etiket="Doğum tarihi"
          type="date"
          value={deger.dogumTarihi ?? ''}
          guven={guven?.dogumTarihi}
          dikkat={dikkat.has('dogumTarihi')}
          hata={eksik.has('dogumTarihi') && bosaMi(deger.dogumTarihi) ? 'KBS bildirimi için zorunlu.' : null}
          onChange={(e) => yaz('dogumTarihi', e.target.value || null)}
        />

        {tcVatandasi && (
          <>
            <Alan
              etiket="Anne adı"
              value={deger.anneAdi ?? ''}
              guven={guven?.anneAdi}
              dikkat={dikkat.has('anneAdi')}
              hata={eksik.has('anneAdi') && bosaMi(deger.anneAdi) ? 'KBS bildirimi için zorunlu.' : null}
              onChange={(e) => yaz('anneAdi', e.target.value || null)}
            />
            <Alan
              etiket="Baba adı"
              value={deger.babaAdi ?? ''}
              guven={guven?.babaAdi}
              dikkat={dikkat.has('babaAdi')}
              hata={eksik.has('babaAdi') && bosaMi(deger.babaAdi) ? 'KBS bildirimi için zorunlu.' : null}
              onChange={(e) => yaz('babaAdi', e.target.value || null)}
            />
          </>
        )}

        <Alan
          etiket="Doğum yeri"
          value={deger.dogumYeri ?? ''}
          guven={guven?.dogumYeri}
          dikkat={dikkat.has('dogumYeri')}
          onChange={(e) => yaz('dogumYeri', e.target.value || null)}
        />
        <Alan
          etiket="Belge seri no"
          value={deger.belgeSeriNo ?? ''}
          guven={guven?.seriNo}
          dikkat={dikkat.has('seriNo')}
          onChange={(e) => yaz('belgeSeriNo', e.target.value.toUpperCase() || null)}
        />
        <Alan
          etiket="Telefon"
          type="tel"
          inputMode="tel"
          value={deger.telefon ?? ''}
          ipucu="Online check-in ve fatura icin"
          onChange={(e) => yaz('telefon', e.target.value || null)}
        />
        <Alan
          etiket="E-posta"
          type="email"
          value={deger.eposta ?? ''}
          onChange={(e) => yaz('eposta', e.target.value || null)}
        />
      </div>

      {(ocr?.dogrulama.mrzCakisanAlanlar.length ?? 0) > 0 && (
        <p className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          MRZ ile on yuzdeki bilgiler su alanlarda ayrisiyor:{' '}
          <strong>{ocr!.dogrulama.mrzCakisanAlanlar.join(', ')}</strong>. Lutfen belgeden kontrol edin.
        </p>
      )}

      {guven && (
        <p className="text-xs text-slate-400">
          Guven esigi %{Math.round(GUVEN_ESIGI * 100)} - altinda kalan alanlar sari isaretlenir.
        </p>
      )}
    </div>
  );
}
